import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { API } from 'aws-amplify';
import { Observable, from } from 'rxjs';
import { DEFAULT_GET_LIMIT } from '../shared/constants/operationals';
import { StripePaymentSource } from './stripe-payment-sources.service';
import { Phone } from './core.module';
import { QineticPaymentSource } from './qinetic-payment-sources.service';

export const ORDERS_API_NAME: string = 'orders';
export const ORDERS_API_ENDPOINT: string = environment.API_BASE_PATH + '/order';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  listOrders(list_params?: ListOrdersParams): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = '';

    let query_string_params: any = {
      cursor: list_params?.cursor || null,
      limit: list_params?.limit || DEFAULT_GET_LIMIT,
    };
    if (list_params?.order_id) query_string_params['order_id'] = list_params.order_id;
    if (list_params?.company_id) query_string_params['company_id'] = list_params.company_id;
    if (list_params?.channel) query_string_params['channel'] = list_params.channel;
    if (list_params?.status) query_string_params['status'] = list_params.status;
    if (list_params?.origin) query_string_params['origin'] = list_params.origin;
    if (list_params?.created_from) query_string_params['created_from'] = list_params.created_from;
    if (list_params?.created_to) query_string_params['created_to'] = list_params.created_to;
    if (list_params?.completed_from) query_string_params['completed_from'] = list_params.completed_from;
    if (list_params?.completed_to) query_string_params['completed_to'] = list_params.completed_to;
    if (list_params?.buyer_name) query_string_params['buyer_name'] = list_params.buyer_name;
    if (list_params?.bd1) query_string_params['bd1'] = list_params.bd1;
    if (list_params?.bd2) query_string_params['bd2'] = list_params.bd2;
    if (list_params?.bd5) query_string_params['bd5'] = list_params.bd5;
    if (list_params?.liquidation) query_string_params['liquidation'] = list_params.liquidation;
    if (list_params?.qinetic_id) query_string_params['qinetic_id'] = list_params.qinetic_id;
    if (list_params?.store_name) query_string_params['store_name'] = list_params.store_name;
    if (list_params?.approved) query_string_params['approved'] = list_params.approved;
    if (list_params?.external_id) query_string_params['external_id'] = list_params.external_id;
    if (list_params?.phone_number) query_string_params['phone_number'] = list_params.phone_number;
    if (list_params?.owner_link) query_string_params['owner_link'] = list_params.owner_link;
    if (list_params?.qinetic_bug) query_string_params['qinetic_bug'] = list_params.qinetic_bug;
    if (list_params?.tax_deduction) query_string_params['tax_deduction'] = list_params.tax_deduction;
    if (list_params?.tax_deduction_type) query_string_params['tax_deduction_type'] = list_params.tax_deduction_type;
    if (list_params?.merchant_refund) query_string_params['merchant_refund'] = list_params.merchant_refund;
    if (list_params?.has_qinetic_id) query_string_params['has_qinetic_id'] = list_params.has_qinetic_id;
    if (list_params?.last_operation) query_string_params['last_operation'] = list_params.last_operation;
    if (list_params?.last_operation_from) query_string_params['last_operation_from'] = list_params.last_operation_from;
    if (list_params?.last_operation_to) query_string_params['last_operation_to'] = list_params.last_operation_to;
    if (list_params?.agreement) query_string_params['agreement'] = list_params.agreement;

    const init = { response: true, queryStringParameters: query_string_params };
    return from(API.get(api_name, path, init));
  }

  read(store_id: string, order_id: string): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = `/${order_id}/store/${store_id}`;
    const init = {
      response: true,
    };
    return from(API.get(api_name, path, init));
  }

  listByStoreID(store_id: string): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = `/store/${store_id}`;
    const init = {
      response: true,
    };
    return from(API.get(api_name, path, init));
  }

  listByCompanyID(company_id: string): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = `/company/${company_id}`;
    const init = {
      response: true,
    };
    return from(API.get(api_name, path, init));
  }

  delete(store_id: string, order_id: string): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = `/${order_id}/store/${store_id}`;
    const init = {
      response: true,
    };
    return from(API.del(api_name, path, init));
  }

  setNote(store_id: string, order_id: string, note: string): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = `/${order_id}/store/${store_id}/note`;
    const init = { response: true, body: { note } };
    return from(API.put(api_name, path, init));
  }

  setIsLookedAt(store_id: string, order_id: string, is_looked_at: boolean): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = `/${order_id}/store/${store_id}/is-looked-at`;
    const init = { response: true, body: { is_looked_at } };
    return from(API.put(api_name, path, init));
  }

  readSummary(): Observable<any> {
    const api_name: string = ORDERS_API_NAME;
    const path: string = '/summary';
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }
}

export interface ListOrdersParams {
  order_id?: string;
  company_id?: string;
  buyer_name?: string;
  status?: OrderStatus;
  channel?: Channel;
  origin?: Origin;
  created_from?: number;
  created_to?: number;
  completed_from?: number;
  completed_to?: number;
  bd1?: string;
  bd2?: string;
  bd5?: string;
  liquidation?: string;
  qinetic_id?: string;
  store_name?: string;
  approved?: string;
  external_id?: string;
  phone_number?: string;
  owner_link?: string;
  qinetic_bug?: string;
  tax_deduction?: string;
  tax_deduction_type?: TaxDeductionType;
  merchant_refund?: VbanStatus;
  has_qinetic_id?: string;
  last_operation?: string;
  last_operation_from?: number;
  last_operation_to?: number;
  agreement?: string;

  cursor?: string | null;
  limit?: number;
}

export class Order {
  order_id?: string;

  store_id?: string;
  buyer_id?: string;
  company_id?: string;
  seller_id?: string;
  references?: OrderReferences;
  amount?: Amount;
  summary?: Summary;
  fee?: Fee;
  status?: OrderStatus;
  origin?: Origin;
  channel?: Channel;
  owner_id?: string;
  cart?: CartItem[];

  contract_id?: string;
  fea_contract_id?: string;

  bd1_evaluation_id?: string;
  bd2_evaluation_id?: string;
  bd5_evaluation_id?: string;
  bd1_result?: string;
  bd2_result?: string;
  bd5_result?: string;

  qinetic_position_id?: string;
  position_id?: string;
  position_error?: any;

  store_name?: string;
  store_logo?: string;

  seller_firstname?: string;
  seller_lastname?: string;
  seller_email?: string;
  seller_phone?: Phone;

  buyer_firstname?: string;
  buyer_lastname?: string;
  buyer_email?: string;
  buyer_phone?: Phone;
  buyer_tax_code?: string;
  buyer_stripe_payment_source?: StripePaymentSource;
  origin_buyer_stripe_payment_source?: StripePaymentSource;
  buyer_qinetic_payment_source?: QineticPaymentSource;

  pending_at?: number;
  store_review_at?: number;
  running_at?: number;
  completed_at?: number;
  canceled_at?: number;
  refunded_at?: number;
  created_at?: number;
  end_date?: number;
  updated_at?: number;
  liquidated_at?: number;
  ready_for_liquidation_at?: number;
  refund_id?: string;

  ext_order_id?: string;
  tax_deduction?: TaxDeduction;

  pre_authorization_at?: number;
  last_experian_verification_id?: string;

  short_url?: string;
  company_custom_buyer_subdomain?: string;
  agreement?: string;

  vban_id?: string;
  vban_status?: VbanStatus;

  last_status_change?: string;
  last_status_change_at?: number;

  is_looked_at?: boolean;
  note?: string;
}

export interface TaxDeduction {
  tax_deduction_type?: string;
  tax_deduction_invoice_number?: string;
  tax_deduction_invoice_date?: number;
}

export class OrderReferences {
  ext_order_id?: string;
  cms?: string;
  plugin_version?: string;
  success_ref?: string;
  failure_ref?: string;
  thank_you_page?: string;
  failure_page?: string;
}

export class Amount {
  total?: number;
  currency?: Currency;
  sp_total_fee?: number;
  stripe_fee?: number;
  net?: number;
  collected?: number;
  refunded?: number;
}

export class Summary {
  total?: number;
  canceled?: number;
  unpaid?: number;
  pending?: number;
  completed?: number;
  refunded?: number;
}

export class Fee {
  splittypay_fee?: number;
  splittypay_fee_fixed?: number;
  stripe_fee?: number;
  stripe_fee_fixed?: number;
}

export class CartItem {
  quantity?: number;
  product?: string;
}

export enum OrderStatus {
  store_review = 'store_review',
  pending = 'pending',
  running = 'running',
  completed = 'completed',
  canceled = 'canceled',
  refunded = 'refunded',
  archived = 'archived',
}

export enum VbanStatus {
  pending = 'pending',
  succeeded = 'succeeded',
  partially_funded = 'partially_funded',
  failed = 'failed',
}

export interface OrderSummary {
  length: number;
  store_review: number;
  pending: number;
  running: number;
  completed: number;
  canceled: number;
  refunded: number;
  archived: number;
}

export enum Currency {
  eur = 'eur',
  dol = 'dol',
}

export enum Origin {
  in_store = 'in_store',
  web = 'web',
}

export enum Channel {
  on_session = 'on_session',
  off_session = 'off_session',
}

export enum BDResult {
  del_pos = 'del_pos',
  del_neg = 'del_neg',
}

export enum Approved {
  yes = 'yes',
  no = 'no',
}

export enum TaxDeductionType {
  bonus_mobili = 'bonus_mobili',
  conto_termico = 'conto_termico',
  conto_termico_3 = 'conto_termico_3',
  interventi_sismici = 'interventi_sismici',
  riqualifica_edilizia = 'riqualifica_edilizia',
  risparmio_energetico = 'risparmio_energetico',
}
